import React, { useState, useCallback } from "react";
import {
  Modal,
  View,
  StyleSheet,
  TouchableOpacity,
  Dimensions,
  TouchableWithoutFeedback,
} from "react-native";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { Calendar } from "react-native-calendars";
import Text from "@components/CustomText";
import dayjs from "dayjs";
import { colors } from "@constants/theme";
import { fontFamily } from "@utils/styles";
import Cross from "@assets/svg/CrossIcon";
import t from "@constants/localization";

interface DateRange {
  startDate: string | null;
  endDate: string | null;
}

interface DateFilterModalProps {
  visible: boolean;
  onClose: () => void;
  onApply: (startDate: string, endDate: string) => void;
  initialDateRange?: DateRange;
  minDate?: string;
  days_limit?: number;
}

interface MarkedDates {
  [date: string]: {
    selected?: boolean;
    startingDay?: boolean;
    endingDay?: boolean;
    color?: string;
    show_text?: string;
    background_color?: string;
    text_color?: string;
  };
}

interface DayProps {
  date: {
    dateString: string;
    day: number;
    month: number;
    year: number;
    timestamp: number;
  };
  state: "disabled" | "today" | "";
  marking?: {
    selected?: boolean;
    startingDay?: boolean;
    endingDay?: boolean;
    color?: string;
    show_text?: string;
    background_color?: string;
    text_color?: string;
  };
}

const DateFilterModal: React.FC<DateFilterModalProps> = ({
  visible,
  onClose,
  onApply,
  initialDateRange,
  days_limit = 10,
}) => {
  // The SafeAreaView COMPONENT measures its own window, and Android Modal
  // windows report zero insets — the hook reads the root window instead.
  const insets = useSafeAreaInsets();
  const [dateRange, setDateRange] = useState<DateRange>(
    initialDateRange || { startDate: "", endDate: "" },
  );
  const [errorMessage, setErrorMessage] = useState<string>("");

  const today = dayjs().format("YYYY-MM-DD");
  const minDate = "2024-03-01";
  const maxDate = today;

  const { width } = Dimensions.get("window");
  const dayBoxWidth = Math.ceil(width / 7); // 7 days week into boxes

  const onDayPress = useCallback(
    (day) => {
      setDateRange((prev) => {
        let newRange;
        if (!prev.startDate || (prev.startDate && prev.endDate)) {
          newRange = { startDate: day.dateString, endDate: null };
        } else if (day.dateString < prev.startDate) {
          newRange = { startDate: day.dateString, endDate: prev.startDate };
        } else {
          newRange = { startDate: prev.startDate, endDate: day.dateString };
        }
        setErrorMessage("");
        if (newRange.startDate && newRange.endDate) {
          const diffDays = dayjs(newRange.endDate).diff(
            dayjs(newRange.startDate),
            "day",
          );
          if (diffDays > days_limit) {
            setErrorMessage(`Select a date range of ${days_limit} days`);
          }
        }
        return newRange;
      });
    },
    [days_limit],
  );

  const getMarkedDates = (): MarkedDates => {
    const { startDate, endDate } = dateRange;
    const markedDates: MarkedDates = {
      [today]: {
        show_text: "आज",
        background_color: colors.white,
        text_color: colors.mediumBlack,
      },
    };

    if (!startDate) return markedDates;

    markedDates[startDate] = {
      selected: true,
      startingDay: true,
      text_color: colors.white,
      background_color: colors.blue3,
      ...(startDate === today && { show_text: "आज" }),
    };

    if (endDate) {
      markedDates[endDate] = {
        selected: true,
        endingDay: true,
        text_color: colors.white,
        background_color: colors.blue3,
        ...(endDate === today && { show_text: "आज" }),
      };

      let current = dayjs(startDate).add(1, "day");
      const end = dayjs(endDate);

      while (current.isBefore(end)) {
        const dateString = current.format("YYYY-MM-DD");
        markedDates[dateString] = {
          selected: true,
          text_color: colors.mediumBlack,
          background_color: colors.blue4,
          ...(dateString === today && { show_text: "आज" }),
        };
        current = current.add(1, "day");
      }
    }

    return markedDates;
  };

  const renderDay = useCallback(
    ({ date, state, marking }: DayProps) => {
      const isDisabled = state === "disabled";
      const { dateString, day } = date;
      const {
        background_color = colors.white,
        text_color = isDisabled ? colors.lightMediumBlack : colors.mediumBlack,
        show_text = "",
      } = marking || {};

      return (
        <TouchableOpacity
          key={dateString}
          onPress={() => onDayPress(date)}
          disabled={isDisabled}
          style={[styles.dayContainer, { backgroundColor: background_color }]}
        >
          {show_text !== "" && (
            <Text
              variant="caption_3"
              newfont={true}
              style={{ color: colors.plain_green }}
            >
              {show_text}
            </Text>
          )}
          <Text
            variant="body_text_3"
            newfont={true}
            style={{ color: text_color }}
          >
            {day}
          </Text>
        </TouchableOpacity>
      );
    },
    [onDayPress],
  );

  const canApply = dateRange.startDate && dateRange.endDate;
  const canClear = dateRange.startDate || dateRange.endDate;

  const calenderTheme = {
    dotColor: colors.primary,
    arrowColor: colors.mediumBlack,
    selectedDayBackgroundColor: colors.blue3,
    textDayFontWeight: "400",
    todayTextColor: colors.mediumBlack,
    todayBackgroundColor: colors.lighterBlack,
    textMonthFontWeight: "100",
    "stylesheet.calendar.header": {
      dayHeader: {
        marginTop: 0,
        marginBottom: 0,
        width: 32,
        textAlign: "center",
        color: colors.mediumBlack,
        fontFamily: fontFamily.semiBold,
        fontSize: 14,
        lineHeight: 18,
      },
      monthText: {
        fontSize: 14,
        fontFamily: fontFamily.semiBold,
        color: colors.lightMediumBlack,
      },
      header: {
        flexDirection: "row",
        justifyContent: "space-between",
        alignItems: "center",
        backgroundColor: colors.white,
        height: 42,
      },
      week: {
        marginTop: 0,
        flexDirection: "row",
        justifyContent: "space-around",
        alignItems: "center",
        backgroundColor: colors.white,
        height: 40,
        borderBottomWidth: 0.5,
        borderColor: colors.blank_grey,
      },
    },
    "stylesheet.day.basic": {
      today: {
        borderColor: colors.primary,
        borderWidth: 0.8,
      },
      todayText: {
        color: colors.primary,
        fontWeight: "800",
      },
      container: {
        alignSelf: "center",
        alignItems: "center",
        justifyContent: "center",
      },
      base: {
        width: 50,
        height: 50,
        alignItems: "center",
      },
    },
    "stylesheet.calendar-list.main": {
      container: {
        backgroundColor: colors.white,
      },
      calendar: {
        paddingLeft: 0,
        paddingRight: 0,
      },
      staticHeader: {
        position: "absolute",
        left: 0,
        right: 0,
        top: 0,
        backgroundColor: colors.white,
        paddingHorizontal: 0,
      },
    },
    "stylesheet.calendar.main": {
      dayContainer: {
        alignItems: "center",
        borderWidth: 0.5,
        borderColor: colors.blank_grey,
        justifyContent: "center",
        width: dayBoxWidth,
        height: 50,
        position: "relative",
        paddingHorizontal: 0,
        paddingVertical: 0,
      },
      week: {
        flexDirection: "row",
        justifyContent: "space-between",
      },
      container: {
        backgroundColor: colors.shadowLighter,
        paddingLeft: 0,
        paddingRight: 0,
      },
      emptyDayContainer: {
        flex: 1,
        width: 50,
        height: 50,
        backgroundColor: colors.shadowLighter,
      },
      monthView: {
        backgroundColor: colors.white,
      },
    },
  };

  return (
    <Modal
      visible={visible}
      transparent={true}
      animationType="slide"
      onRequestClose={onClose}
      statusBarTranslucent
      navigationBarTranslucent
    >
      <TouchableWithoutFeedback onPress={onClose}>
        <View style={styles.container} />
      </TouchableWithoutFeedback>
      <View style={styles.container}>
        <View style={[styles.content, { paddingBottom: insets.bottom }]}>
          <View style={styles.header}>
            <Text variant="title_16" newfont={true} style={styles.title}>
              {t("LedgerTxns/DateFilterModal/title")}
            </Text>
            <TouchableOpacity onPress={onClose} style={styles.closeButton}>
              <Cross width={18} height={18} />
            </TouchableOpacity>
          </View>

          <View style={styles.calendarContainer}>
            <Calendar
              horizontal={true}
              pagingEnabled={false}
              calendarHeight={300}
              markingType="custom"
              markedDates={getMarkedDates()}
              onDayPress={onDayPress}
              minDate={minDate}
              maxDate={maxDate}
              pastScrollRange={2}
              futureScrollRange={2}
              theme={calenderTheme}
              style={styles.calendar}
              dayComponent={renderDay}
            />
            <View style={styles.btnerrorContainer}>
              {errorMessage ? (
                <Text variant="caption_2" style={styles.errorText}>
                  {errorMessage}
                </Text>
              ) : null}
              <View style={styles.buttonContainer}>
                <TouchableOpacity
                  style={[
                    styles.submitButton,
                    !canClear && styles.submitButtonDisabled,
                  ]}
                  disabled={!canClear}
                  onPress={() => {
                    setDateRange({ startDate: null, endDate: null });
                    onApply(null, null);
                    setErrorMessage("");
                    onClose();
                  }}
                >
                  <Text
                    variant="title_16"
                    style={[
                      styles.submitButtonText,
                      !canClear && styles.submitButtonTextDisabled,
                    ]}
                  >
                    {t("LedgerTxns/DateFilterModal/clear")}
                  </Text>
                </TouchableOpacity>
                <TouchableOpacity
                  style={[
                    styles.submitButton,
                    (!canApply || errorMessage) && styles.submitButtonDisabled,
                  ]}
                  onPress={() => {
                    if (canApply && !errorMessage) {
                      onApply(dateRange.startDate, dateRange.endDate);
                      onClose();
                    }
                  }}
                >
                  <Text
                    variant="title_16"
                    style={[
                      styles.submitButtonText,
                      (!canApply || errorMessage) &&
                        styles.submitButtonTextDisabled,
                    ]}
                  >
                    {t("LedgerTxns/DateFilterModal/apply")}
                  </Text>
                </TouchableOpacity>
              </View>
            </View>
          </View>
        </View>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.modalBackground,
  },
  content: {
    backgroundColor: colors.white,
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    marginTop: "auto",
  },
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    padding: 16,
  },
  title: {
    color: colors.mediumBlack,
  },
  closeButton: {
    position: "absolute",
    right: 10,
    top: -35,
    backgroundColor: colors.white,
    borderRadius: 50,
    zIndex: 1,
    padding: 6,
  },
  calendarContainer: {
    backgroundColor: colors.white,
  },
  calendar: {
    marginBottom: 16,
  },
  buttonContainer: {
    padding: 16,
    flexDirection: "row",
    justifyContent: "space-between",
    gap: 12,
  },
  btnerrorContainer: {
    borderTopWidth: 1,
    borderTopColor: colors.lineColor,
  },
  submitButton: {
    flex: 1,
    backgroundColor: colors.btnGreen,
    paddingVertical: 12,
    borderRadius: 8,
    justifyContent: "center",
    alignItems: "center",
  },
  submitButtonDisabled: {
    backgroundColor: colors.disableGrey,
  },
  submitButtonText: {
    color: colors.white,
  },
  submitButtonTextDisabled: {
    color: colors.white,
  },
  dayContainer: {
    alignItems: "center",
    justifyContent: "center",
    width: "100%",
    height: 50,
  },
  errorText: {
    color: colors.red,
    textAlign: "center",
    marginTop: 8,
  },
});

export default DateFilterModal;
